import type { WorkflowConfig } from './config';

/**
 * Whether an account may use a workflow that requires pre-authorization.
 *
 * Mirrors `isCaptureAuthorized` on the server, which is the authority — this only decides what to show, so
 * a workflow never appears as available and then fails at submit.
 *
 * DOCE and SWFT keep their own account flags because hundreds of live accounts carry them; anything newly
 * configured is authorized through `authorizedCaptureTypes`, so adding a gated workflow needs no new
 * account field and no migration. This used to be two hardcoded maps in two components, which is why a
 * newly configured workflow could not be gated at all.
 */
export interface AuthorizingAccount {
  swftEnabled?: boolean | undefined;
  doceEnabled?: boolean | undefined;
  authorizedCaptureTypes?: string[] | undefined;
}

export function isWorkflowAuthorized(
  account: AuthorizingAccount | undefined,
  wf: Pick<WorkflowConfig, 'captureType' | 'requiresAuthorization'>,
): boolean {
  if (!wf.requiresAuthorization) return true;
  if (!account) return false;
  if (account.authorizedCaptureTypes?.includes(wf.captureType)) return true;
  // One switch means "this customer does SWFT", covering its card-conversion variant too.
  if (wf.captureType === 'swft' || wf.captureType === 'conv-swft') return Boolean(account.swftEnabled);
  if (wf.captureType === 'doce') return Boolean(account.doceEnabled);
  return false;
}
